/**
 * 알림 설정 /me/notifications (히어로챗 /settings/notifications 판, D110).
 *
 * - 「앱 알림」 스위치 하나: 켜면 기기 알림 권한을 묻고(아직이면) 이 기기를 등록, 끄면 이 기기 토큰을 서버에서 지움.
 *   기기에서 막혀 있으면(denied) 기기 설정에서 켜라는 안내만(앱이 대신 켤 수 없음). 앱에서 돌아오면 다시 읽음
 * - 무엇을 받을지는 아래의 「알림 목록」 스위치가 정합니다.
 * - 웹은 보낼 길이 없어(웹 푸시 없음) 앱에서만 쓸 수 있다고 알립니다
 */
import { useEffect, useState } from 'react';
import { NOTIFICATION_KINDS } from '@config/notifications/index.js';
import { Card } from '../../components/Card.jsx';
import { Switch } from '../../components/Switch.jsx';
import { TopBar } from '../../components/TopBar.jsx';
import { useGoBack } from '../../hooks/useGoBack.js';
import { disablePush, enablePush, isNativeApp, pushEnabled, pushPermission } from '../../lib/push.js';
import { errorMessageOf } from '../../api/client.js';
import * as meApi from '../../api/me.js';

const DEFAULT_NOTIFICATIONS = NOTIFICATION_KINDS.map(({ kind, name, defaultOn }) => ({
    kind,
    name,
    enabled: defaultOn,
}));

export function NotificationSettingsPage() {
    const goBack = useGoBack('/me');
    const native = isNativeApp();
    const [permission, setPermission] = useState(null);
    const [enabled, setEnabled] = useState(pushEnabled);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [notifications, setNotifications] = useState(DEFAULT_NOTIFICATIONS);
    const [notifyError, setNotifyError] = useState(null);
    const on = native && permission === 'granted' && enabled;

    useEffect(() => {
        const refresh = () => {
            if (document.visibilityState !== 'visible') {
                return;
            }
            pushPermission().then(setPermission);
            setEnabled(pushEnabled());
        };
        refresh();
        // 기기 설정에서 권한을 바꾸고 돌아오면 다시 읽습니다
        document.addEventListener('visibilitychange', refresh);
        return () => document.removeEventListener('visibilitychange', refresh);
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        meApi.fetchNotifications({ signal: controller.signal })
            .then((response) => setNotifications(response.notifications))
            .catch(() => {
                // 기본값을 보여 줍니다. 바꾸려고 누르면 그때 오류를 알립니다
            });
        return () => controller.abort();
    }, []);

    async function toggleNotification(kind, next) {
        const previous = notifications;
        setNotifyError(null);
        setNotifications((items) => items.map((item) => (item.kind === kind ? { ...item, enabled: next } : item)));
        try {
            const response = await meApi.updateNotification(kind, next);
            setNotifications(response.notifications);
        } catch (error) {
            setNotifications(previous);
            setNotifyError(errorMessageOf(error, '알림을 바꾸지 못했어요. 잠시 후 다시 시도해 주세요.'));
        }
    }

    async function toggle(next) {
        setBusy(true);
        setMessage('');
        try {
            if (next) {
                const result = await enablePush();
                setPermission(result);
                if (result !== 'granted') {
                    setMessage('기기 설정에서 인생상회 알림을 허용해 주세요.');
                }
            } else {
                const removed = await disablePush();
                if (!removed) {
                    setMessage('이 기기 등록을 지우지 못했어요. 잠시 뒤 다시 해 주세요.');
                }
            }
            setEnabled(pushEnabled());
        } catch {
            setMessage('알림 설정을 바꾸지 못했어요. 잠시 뒤 다시 해 주세요.');
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="flex h-dvh flex-col bg-bg">
            <TopBar title="알림 설정" onBack={goBack} />
            <main className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-gutter pt-5 pb-[calc(24px+env(safe-area-inset-bottom,0px))]">
                {!native ? (
                    <p className="m-0 text-caption leading-5 text-text-secondary">알림은 앱에서만 받을 수 있어요.</p>
                ) : null}
                <Card className="px-4 py-0">
                    <div className="flex min-h-[68px] items-center justify-between gap-3 py-2">
                        <span className="flex flex-col gap-0.5">
                            <span className="text-[15px] font-semibold">앱 알림</span>
                            <span className="text-caption text-text-secondary">이 기기로 알림을 받아요.</span>
                        </span>
                        <Switch
                            checked={on}
                            label="앱 알림"
                            disabled={!native || busy || permission === null}
                            onChange={toggle}
                        />
                    </div>
                </Card>
                {native && permission === 'denied' && !on && !message ? (
                    <p className="m-0 text-caption leading-5 text-text-secondary">
                        기기에서 알림이 꺼져 있어요. 기기 설정에서 인생상회 알림을 허용해 주세요.
                    </p>
                ) : null}
                {message ? (
                    <p role="status" className="m-0 text-caption leading-5 text-text-secondary">
                        {message}
                    </p>
                ) : null}
                <section className="mt-4 flex flex-col gap-2">
                    <h2 className="m-0 text-section text-label-muted">알림 목록</h2>
                    <Card className="px-4 py-0">
                        {notifications.map((item, index) => (
                            <div
                                key={item.kind}
                                className={`flex h-[60px] items-center justify-between ${
                                    index < notifications.length - 1 ? 'border-b border-hairline' : ''
                                }`}
                            >
                                <span className="text-[15px]">{item.name}</span>
                                <Switch
                                    checked={item.enabled}
                                    label={`${item.name} 알림`}
                                    onChange={(next) => toggleNotification(item.kind, next)}
                                />
                            </div>
                        ))}
                    </Card>
                    {notifyError ? (
                        <p role="alert" className="m-0 text-caption text-error">{notifyError}</p>
                    ) : null}
                </section>
            </main>
        </div>
    );
}
